import { Fragment, memo, type ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { useVirtualizer } from '@tanstack/react-virtual'
import { clsx } from 'clsx'
import { DoorOpen } from 'lucide-react'

import styles from './GtTurnList.module.scss'

import type { GtKeyword, GtSpeaker, GtTurn, GtTurnState, MeetingAttributionGtSnapshotTurn } from '@/api/internal'
import { toSpeakerIdentity } from '@/pages/app/AttributionViewer/annotate/gtDerivations'
import {
  type BlockTail,
  blockTail,
  type KeywordRange,
  keywordRanges,
  segmentText,
} from '@/pages/app/AttributionViewer/annotate/gtEmphasis'
import { playheadCaret } from '@/pages/app/AttributionViewer/annotate/gtModel'
import { type SpeakerStyleMap, styleForSpeaker } from '@/pages/app/AttributionViewer/components/colors'
import { formatMs } from '@/pages/app/AttributionViewer/components/Swimlane'

export const gtTurnRowId = (turnId: string): string => `gt-turn-${turnId}`

export type GtRowEditMode = 'text' | 'note' | 'split'

export interface GtRowEditor {
  turnId: string
  mode: GtRowEditMode
  node: ReactNode
}

const EDITOR_LABEL: Record<GtRowEditMode, string> = {
  text: 'Edit text',
  note: 'Note',
  split: 'Split text',
}

const EDITOR_HINT: Partial<Record<GtRowEditMode, string>> = {
  text: '⏎ splits at cursor · ⌘⏎ saves · Esc cancels',
  split: '⏎ splits at cursor · Esc cancels',
}

const CONFIRM_FLASH_MS = 1200
const EMPTY_KEYWORDS_BY_SOURCE_TURN_ID: Record<string, GtKeyword[]> = {}

/** Caret offset inside `container` for the current selection, e.g. the word a double-click landed on. */
const selectionOffsetIn = (container: HTMLElement): number | null => {
  const selection = window.getSelection()
  if (!selection || selection.rangeCount === 0 || !selection.anchorNode) return null
  if (!container.contains(selection.anchorNode)) return null
  const range = document.createRange()
  range.setStart(container, 0)
  range.setEnd(selection.anchorNode, selection.anchorOffset)
  return range.toString().length
}

interface GtTurnTextProps {
  turnId: string
  text: string
  ranges: readonly KeywordRange[]
  tail: BlockTail | null
  caret: number | null
  focusedOffset: number | null
  isDeleted: boolean
  isEdited: boolean
  onEditRequest?: (turnId: string, caret: number | null) => void
}

const GtTurnText = memo(function GtTurnText({
  turnId,
  text,
  ranges,
  tail,
  caret,
  focusedOffset,
  isDeleted,
  isEdited,
  onEditRequest,
}: GtTurnTextProps) {
  const textParts = useMemo(
    () => segmentText(text, ranges, tail, caret, focusedOffset),
    [text, ranges, tail, caret, focusedOffset]
  )

  return (
    <>
      <span
        className={clsx(styles.text, isDeleted && styles.textDeleted)}
        onDoubleClick={
          onEditRequest ? (event) => onEditRequest(turnId, selectionOffsetIn(event.currentTarget)) : undefined
        }
      >
        {textParts.segments.map((segment) => {
          const matched = segment.keyword
          const content = matched ? (
            <strong
              className={clsx(styles.keyword, matched.note && styles.keywordSuspect)}
              data-category={matched.category}
              title={`${matched.category[0].toUpperCase()}${matched.category.slice(1)}${
                matched.note ? ` — ${matched.note}` : ''
              }`}
            >
              {segment.text}
            </strong>
          ) : (
            segment.text
          )
          return (
            <Fragment key={segment.start}>
              {textParts.caretOffset === segment.start && <span className={styles.playhead} aria-hidden="true" />}
              <span className={clsx(segment.played && styles.played, segment.focused && styles.stopFocused)}>
                {segment.inTail ? (
                  <em className={clsx(styles.tail, tail && !tail.complete && styles.tailIncomplete)}>{content}</em>
                ) : (
                  content
                )}
              </span>
            </Fragment>
          )
        })}
        {textParts.caretOffset === text.length && <span className={styles.playhead} aria-hidden="true" />}
        {isEdited && <span className={styles.editedMark}>✎ edited</span>}
      </span>
    </>
  )
})

/** Ids of turns that just became confirmed, held for one flash so each accept is visible as you move on. */
const useConfirmFlash = (turns: readonly GtTurn[]): ReadonlySet<string> => {
  const previousRef = useRef<Map<string, GtTurnState> | null>(null)
  const timersRef = useRef<number[]>([])
  const [flashing, setFlashing] = useState<ReadonlySet<string>>(() => new Set())

  useEffect(() => {
    const previous = previousRef.current
    previousRef.current = new Map(turns.map((turn) => [turn.id, turn.state]))
    if (!previous) return
    const fresh = turns.flatMap((turn) =>
      turn.state === 'confirmed' && previous.has(turn.id) && previous.get(turn.id) !== 'confirmed' ? [turn.id] : []
    )
    if (fresh.length === 0) return
    setFlashing((ids) => new Set([...Array.from(ids), ...fresh]))
    const timer = window.setTimeout(() => {
      timersRef.current = timersRef.current.filter((id) => id !== timer)
      setFlashing((ids) => new Set(Array.from(ids).filter((id) => !fresh.includes(id))))
    }, CONFIRM_FLASH_MS)
    timersRef.current.push(timer)
  }, [turns])

  useEffect(() => () => timersRef.current.forEach((timer) => window.clearTimeout(timer)), [])

  return flashing
}

export interface GtTurnListProps {
  turns: readonly GtTurn[]
  speakersById: Map<string, GtSpeaker>
  /** Speaker id -> the name it is shown and scored under (room voices read "<room> · voice N"). */
  displayNames?: ReadonlyMap<string, string>
  snapshotByTurnId: Record<string, MeetingAttributionGtSnapshotTurn>
  activeTurnId: string | null
  speakerStyles: SpeakerStyleMap
  editor?: GtRowEditor
  /** Audio position; the active row marks how far into its text playback has reached. */
  playheadMs?: number | null
  keywordsBySourceTurnId?: Record<string, GtKeyword[]>
  focusedStop?: { turnId: string; offset: number } | null
  onSelect: (turnId: string) => void
  selectedSpeakerIds?: ReadonlySet<string>
  onToggleSpeaker?: (speakerId: string) => void
  onReselect?: (turnId: string) => void
  onAssignRequest?: (turnId: string) => void
  /** Double-click on a row's text; `caret` is where in the text the click landed. */
  onEditRequest?: (turnId: string, caret: number | null) => void
}

export const GtTurnList = ({
  turns,
  speakersById,
  displayNames,
  snapshotByTurnId,
  activeTurnId,
  speakerStyles,
  editor,
  playheadMs,
  keywordsBySourceTurnId = EMPTY_KEYWORDS_BY_SOURCE_TURN_ID,
  focusedStop = null,
  onSelect,
  selectedSpeakerIds,
  onToggleSpeaker,
  onReselect,
  onAssignRequest,
  onEditRequest,
}: GtTurnListProps) => {
  const bodyRef = useRef<HTMLDivElement>(null)
  const flashing = useConfirmFlash(turns)
  const emphasisByTurnId = useMemo(() => {
    const emphasis = new Map<string, { tail: BlockTail | null; ranges: KeywordRange[] }>()
    for (const turn of turns) {
      emphasis.set(turn.id, {
        tail: blockTail(turn.text),
        ranges: keywordRanges(turn.text, keywordsBySourceTurnId[turn.source_turn_id] ?? []),
      })
    }
    return emphasis
  }, [turns, keywordsBySourceTurnId])

  const activeIndex = useMemo(
    () => (activeTurnId ? turns.findIndex((t) => t.id === activeTurnId) : -1),
    [turns, activeTurnId]
  )

  const virtualizer = useVirtualizer({
    count: turns.length,
    getScrollElement: () => bodyRef.current,
    estimateSize: () => 44,
    overscan: 10,
    getItemKey: (index) => turns[index].id,
  })

  useEffect(() => {
    if (activeIndex >= 0) virtualizer.scrollToIndex(activeIndex, { align: 'auto' })
  }, [activeIndex, virtualizer])

  if (turns.length === 0) {
    return (
      <div className={styles.list} ref={bodyRef}>
        <div className={styles.empty}>No turns in this view.</div>
      </div>
    )
  }

  return (
    <div
      className={styles.list}
      ref={bodyRef}
      role="listbox"
      aria-label="Turns"
      tabIndex={0}
      aria-activedescendant={activeTurnId ? gtTurnRowId(activeTurnId) : undefined}
    >
      <div style={{ height: virtualizer.getTotalSize(), position: 'relative' }}>
        {virtualizer.getVirtualItems().map((virtualRow) => {
          const turn = turns[virtualRow.index]
          const speaker = turn.speaker_id ? (speakersById.get(turn.speaker_id) ?? null) : null
          const style = speaker ? styleForSpeaker(toSpeakerIdentity(speaker), speakerStyles) : null
          const speakerName = speaker ? (displayNames?.get(speaker.id) ?? speaker.name) : null
          const isRoomVoice = speaker?.kind === 'room'
          const speakerSelected = speaker != null && selectedSpeakerIds?.has(speaker.id)
          const snapshot = snapshotByTurnId[turn.source_turn_id] ?? null
          const isActive = turn.id === activeTurnId
          const isDeleted = turn.state === 'deleted'
          const isConfirmed = turn.state === 'confirmed'
          const caret = isActive && playheadMs != null ? playheadCaret(turn, turn.text, playheadMs) : null
          const focusedOffset = focusedStop?.turnId === turn.id ? focusedStop.offset : null
          const emphasis = emphasisByTurnId.get(turn.id)
          const tail = emphasis?.tail ?? null
          const ranges = emphasis?.ranges ?? []
          const isEdited = snapshot != null && snapshot.text !== turn.text
          const hint = snapshot?.hint
          const rowEditor = editor?.turnId === turn.id ? editor : null
          const editorNode = rowEditor && (
            <div className={styles.editor} onClick={(event) => event.stopPropagation()}>
              <span className={styles.editorLabel}>{EDITOR_LABEL[rowEditor.mode]}</span>
              <span className={styles.editorBody}>
                {rowEditor.node}
                {EDITOR_HINT[rowEditor.mode] && (
                  <span className={styles.editorHint}>{EDITOR_HINT[rowEditor.mode]}</span>
                )}
              </span>
            </div>
          )
          return (
            <div
              key={virtualRow.key}
              id={gtTurnRowId(turn.id)}
              ref={virtualizer.measureElement}
              data-index={virtualRow.index}
              role="option"
              aria-selected={isActive}
              aria-description={speakerSelected ? 'Selected for room grouping' : undefined}
              data-speaker-selection-target
              data-speaker-selected={speakerSelected || undefined}
              data-state={turn.state ?? undefined}
              className={clsx(
                styles.row,
                isActive && styles.rowActive,
                speakerSelected && styles.rowSpeakerSelected,
                turn.state && styles.rowReviewed,
                flashing.has(turn.id) && styles.rowFlash
              )}
              style={{ transform: `translateY(${virtualRow.start}px)` }}
              onClick={(event) => {
                if (event.metaKey || event.ctrlKey) {
                  event.preventDefault()
                  if (speaker && onToggleSpeaker) onToggleSpeaker(speaker.id)
                  return
                }
                if (event.detail > 1) return
                if (isActive) onReselect?.(turn.id)
                else onSelect(turn.id)
              }}
            >
              <span className={styles.time}>{formatMs(turn.start_ms)}</span>
              <span className={styles.who}>
                <span className={styles.dot} style={{ backgroundColor: style?.color ?? 'transparent' }} />
                {isRoomVoice && (
                  <span className={styles.roomMark} title="Room voice">
                    <DoorOpen size={11} aria-hidden="true" />
                  </span>
                )}
                {onAssignRequest ? (
                  <button
                    type="button"
                    className={clsx(
                      styles.name,
                      styles.nameButton,
                      !speaker && styles.namePending,
                      isRoomVoice && styles.nameRoom
                    )}
                    aria-label={`Assign this turn, currently ${speakerName ?? 'unassigned'}`}
                    aria-haspopup="dialog"
                    onClick={(event) => {
                      event.stopPropagation()
                      onAssignRequest(turn.id)
                    }}
                  >
                    {speakerName ?? 'Unassigned'}
                  </button>
                ) : (
                  <span className={clsx(styles.name, !speaker && styles.namePending, isRoomVoice && styles.nameRoom)}>
                    {speakerName ?? 'Unassigned'}
                  </span>
                )}
              </span>
              <span className={styles.main}>
                {rowEditor && rowEditor.mode !== 'note' ? (
                  editorNode
                ) : (
                  <GtTurnText
                    turnId={turn.id}
                    text={turn.text}
                    ranges={ranges}
                    tail={tail}
                    caret={caret}
                    focusedOffset={focusedOffset}
                    isDeleted={isDeleted}
                    isEdited={isEdited}
                    onEditRequest={onEditRequest}
                  />
                )}
                {isActive && hint && hint.why.length > 0 && (
                  <span className={styles.why}>
                    hint: {hint.name} ({hint.why.join(' · ')})
                  </span>
                )}
                {rowEditor?.mode === 'note'
                  ? editorNode
                  : turn.note && <span className={styles.note}>{turn.note}</span>}
              </span>
              <span className={styles.badges}>
                {isConfirmed && (
                  <span className={clsx(styles.pill, styles.confirmed)} title="Confirmed">
                    ✓
                  </span>
                )}
                {isDeleted && <span className={clsx(styles.pill, styles.deleted)}>✕ Deleted</span>}
                {snapshot?.production && snapshot.production.label !== speakerName && (
                  <span className={clsx(styles.pill, styles.prod)}>prod: {snapshot.production.label}</span>
                )}
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}
